import { addNode } from '../graph/graphModel.js';
import { gpioNodeEventChoices, isGpioNode } from './gpioLogic.js';
import { connect, findNode, getActionTriggers, getEventNodesForSource, isFlowNode } from './logicGraph.js';
import { createLogicNode } from './logicNodeFactory.js';
import { LOGIC_PLACEMENT, nextFreePosition } from './logicPlacement.js';

/** The events of a GPIO entity that report a new state: on and off, or a sensor's `changed`. */
function stateChangeEvents(choices) {
    if (choices.includes('on')) return ['on', 'off'].filter(event => choices.includes(event));
    return choices.includes('changed') ? ['changed'] : [];
}

/**
 * Complete a `connect` result that wired the State of a GPIO entity to the
 * `subject` of a condition or switch nothing triggers yet: the entity's
 * state-change events become its trigger, through the GPIO node's event
 * nodes for them (added when missing). A comparison only runs from an event
 * (docs/GPIO-LOGIC-FIRMWARE-SPEC.md §6); without one it never exports.
 *
 * @param {{ graph, wire } | { graph, error }} result
 * @returns the result, its graph with the trigger when that applies
 */
export function withSubjectTrigger(result, context = {}) {
    const wire = result.wire;
    if (wire?.to.port !== 'subject') return result;
    const gpio = findNode(result.graph, wire.from.node);
    const target = findNode(result.graph, wire.to.node);
    // Without an entity its events are unknown; the validator asks for one first.
    if (!isGpioNode(gpio) || !gpio.data?.entity || !isFlowNode(target)) return result;
    if (getActionTriggers(result.graph, target.id).length > 0) return result;
    const source = { node: gpio.id, port: 'events' };
    let graph = result.graph;
    for (const event of stateChangeEvents(gpioNodeEventChoices(gpio, context))) {
        let node = getEventNodesForSource(graph, source).find(item => item.data?.event === event);
        if (!node) {
            const position = nextFreePosition(graph, { x: target.x - LOGIC_PLACEMENT.actionColumnOffset, y: target.y });
            node = createLogicNode('event', { ...position, preset: { event } });
            const sourced = connect(addNode(graph, node), source, { node: node.id, port: 'source' }, context);
            if (sourced.error) continue;
            graph = sourced.graph;
        }
        const triggered = connect(graph, { node: node.id, port: 'actions' }, { node: target.id, port: 'trigger' }, context);
        if (!triggered.error) graph = triggered.graph;
    }
    return { ...result, graph };
}
